import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './FlufConnectPage.module.css';

// Partnership page for the FLUF Connect cross-listing integration. Linked from
// the "For sellers" band on the landing page and from the footer Quick Links
// (a Console edit). FLUF asked for a backlink, so the links to fluf.io are
// plain follow links (no rel="nofollow").
const FLUF_URL = 'https://fluf.io/';
const FLUF_CAUSEE_CHANNEL_URL = 'https://fluf.io/channels/sell-on-causee';

const FEATURES = [
  {
    titleId: 'FlufConnectPage.features.crosslist.title',
    textId: 'FlufConnectPage.features.crosslist.text',
  },
  {
    titleId: 'FlufConnectPage.features.edit.title',
    textId: 'FlufConnectPage.features.edit.text',
  },
  {
    titleId: 'FlufConnectPage.features.delist.title',
    textId: 'FlufConnectPage.features.delist.text',
  },
];

const STEPS = [
  {
    titleId: 'FlufConnectPage.steps.account.title',
    textId: 'FlufConnectPage.steps.account.text',
  },
  {
    titleId: 'FlufConnectPage.steps.connect.title',
    textId: 'FlufConnectPage.steps.connect.text',
  },
  {
    titleId: 'FlufConnectPage.steps.push.title',
    textId: 'FlufConnectPage.steps.push.text',
  },
];

const ExternalLink = ({ href, className, children }) => (
  <a className={className} href={href} target="_blank" rel="noopener">
    {children}
  </a>
);

export const FlufConnectPageComponent = props => {
  const { intl, scrollingDisabled } = props;

  const channelLink = chunks => (
    <ExternalLink className={css.inlineLink} href={FLUF_CAUSEE_CHANNEL_URL}>
      {chunks}
    </ExternalLink>
  );

  return (
    <Page
      title={intl.formatMessage({ id: 'FlufConnectPage.schemaTitle' })}
      description={intl.formatMessage({ id: 'FlufConnectPage.schemaDescription' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          {/* Hero */}
          <div className={css.hero}>
            <div className={css.heroInner}>
              <span className={css.heroEyebrow}>
                <FormattedMessage id="FlufConnectPage.hero.eyebrow" />
              </span>
              <h1 className={css.heroTitle}>
                <FormattedMessage id="FlufConnectPage.hero.title" />
              </h1>
              <p className={css.heroDek}>
                <FormattedMessage id="FlufConnectPage.hero.dek" />
              </p>
              <div className={css.ctas}>
                <ExternalLink className={css.primaryCta} href={FLUF_URL}>
                  <FormattedMessage id="FlufConnectPage.cta.fluf" />
                </ExternalLink>
              </div>
            </div>
          </div>

          {/* What is FLUF Connect */}
          <div className={css.section}>
            <div className={css.contentWrapper}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="FlufConnectPage.about.heading" />
              </h2>
              <p className={css.sectionLede}>
                <FormattedMessage id="FlufConnectPage.about.body1" />
              </p>
              <p className={css.sectionLede}>
                <FormattedMessage id="FlufConnectPage.about.body2" />
              </p>
            </div>
          </div>

          {/* What it does on Causee */}
          <div className={`${css.section} ${css.sectionTint}`}>
            <div className={css.contentWrapper}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="FlufConnectPage.features.heading" />
              </h2>
              <div className={css.cards}>
                {FEATURES.map(f => (
                  <div className={css.card} key={f.titleId}>
                    <h3 className={css.cardTitle}>
                      <FormattedMessage id={f.titleId} />
                    </h3>
                    <p className={css.cardText}>
                      <FormattedMessage id={f.textId} />
                    </p>
                  </div>
                ))}
              </div>
              <p className={css.note}>
                <strong>
                  <FormattedMessage id="FlufConnectPage.features.note.title" />
                </strong>{' '}
                <FormattedMessage id="FlufConnectPage.features.note.text" />
              </p>
            </div>
          </div>

          {/* How to get started */}
          <div className={css.section}>
            <div className={css.contentWrapper}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="FlufConnectPage.steps.heading" />
              </h2>
              <ol className={css.steps}>
                {STEPS.map((s, i) => (
                  <li className={css.step} key={s.titleId}>
                    <span className={css.stepNumber} aria-hidden="true">
                      {i + 1}
                    </span>
                    <span className={css.stepText}>
                      <strong>
                        <FormattedMessage id={s.titleId} />
                      </strong>
                      <span>
                        <FormattedMessage id={s.textId} />
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
              <p className={css.sectionLede}>
                <FormattedMessage
                  id="FlufConnectPage.steps.moreInfo"
                  values={{ link: channelLink }}
                />
              </p>
              <div className={css.ctas}>
                <ExternalLink className={css.primaryCta} href={FLUF_URL}>
                  <FormattedMessage id="FlufConnectPage.cta.fluf" />
                </ExternalLink>
                <NamedLink className={css.secondaryCta} name="BulkUploadPage">
                  <FormattedMessage id="FlufConnectPage.cta.bulk" />
                </NamedLink>
              </div>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const FlufConnectPage = compose(connect(mapStateToProps), injectIntl)(FlufConnectPageComponent);

export default FlufConnectPage;
